import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, Inbox, Save, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { OUTBOUND_OUTBOX_ACTION_URL, OUTBOUND_OUTBOX_URL, outboundOutboxItemUrl } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Btn, inputCls, OUTBOX_TONE, OutboundHeader, Pill, VERIFY_TONE } from "./ui";

/**
 * Outbound -> Outbox. Every email to a prospect stops here. Approving runs the
 * pre-send check at once (address verified, not suppressed, footer address,
 * no placeholders or banned phrases, not already a lead) so a problem shows
 * now, not at send time. Editing an approved email sends it back to draft.
 */

const STATUSES = [
    ["draft", "To approve"], ["blocked", "Blocked"], ["approved", "Approved"],
    ["sent", "Sent"], ["failed", "Failed"], ["rejected,cancelled", "Rejected / cancelled"], ["all", "All"],
];

function ItemCard({ item, onSaved }) {
    const { update } = useApiMethods();
    const [subject, setSubject] = useState(item.subject);
    const [body, setBody] = useState(item.body);
    const editable = !["sent", "sending"].includes(item.status);
    const dirty = subject !== item.subject || body !== item.body;
    useEffect(() => { setSubject(item.subject); setBody(item.body); }, [item.id, item.subject, item.body]);

    const save = async () => {
        const res = await update(outboundOutboxItemUrl(item.id), { subject, body });
        if (res?.status === "success") { ToastMsg("success", "Saved. It needs approving again."); onSaved(); }
        else ToastMsg("error", res?.message || "Could not save");
    };
    const words = body.trim() ? body.trim().split(/\s+/).length : 0;

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-gray-900 dark:text-white">
                    {item.kind === "reply" ? "Reply to their answer" : `Email ${item.step}`}
                </span>
                <span className="flex items-center gap-2">
                    {item.send_after && item.status === "approved" && <span className="text-gray-500">goes after {item.send_after}</span>}
                    {item.sent_at && <span className="text-gray-500">sent {item.sent_at}</span>}
                    <Pill tone={OUTBOX_TONE[item.status]}>{item.status}</Pill>
                </span>
            </div>
            {item.gate_errors.length > 0 && (
                <ul className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-2 space-y-0.5 text-red-800 dark:text-red-200">
                    {item.gate_errors.map(g => <li key={g} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />{g}</li>)}
                </ul>
            )}
            {item.failure_reason && <p className="text-amber-700 dark:text-amber-300">{item.failure_reason}</p>}
            <input value={subject} onChange={e => setSubject(e.target.value)} disabled={!editable}
                placeholder={item.step > 1 ? "(empty: sent as a reply in the same thread)" : "Subject"} className={inputCls} />
            <textarea value={body} onChange={e => setBody(e.target.value)} disabled={!editable}
                rows={Math.min(14, Math.max(5, body.split("\n").length + 1))} className={cn(inputCls, "font-sans leading-relaxed")} />
            <div className="flex items-center justify-between">
                <span className={cn("text-[11px]", words > 130 ? "text-amber-600" : "text-gray-400")}>{words} words · footer with your address and unsubscribe link is added when sent</span>
                {editable && dirty && <Btn onClick={save}><Save className="h-3.5 w-3.5" /> Save edit</Btn>}
            </div>
        </div>
    );
}

export default function Outbox() {
    const { fetch, create } = useApiMethods();
    const [params, setParams] = useSearchParams();
    const status = params.get("status") || "draft";
    const sequence = params.get("sequence") || "";
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        fetch(OUTBOUND_OUTBOX_URL, { status, sequence }).then(res => {
            if (res?.status === "success") setData(res);
            else ToastMsg("error", res?.message || "Could not load outbox");
        });
    }, [status, sequence]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const groups = useMemo(() => {
        const out = [];
        const byId = {};
        (data?.data || []).forEach(i => {
            if (!byId[i.enrollment_id]) { byId[i.enrollment_id] = { ...i, items: [] }; out.push(byId[i.enrollment_id]); }
            byId[i.enrollment_id].items.push(i);
        });
        return out;
    }, [data]);

    const act = async (action, ids) => {
        const res = await create(OUTBOUND_OUTBOX_ACTION_URL, { action, ids });
        if (res?.status === "success") {
            ToastMsg(Object.keys(res.blocked || {}).length ? "warning" : "success", res.message);
            load();
        } else ToastMsg("error", res?.message || "Could not do that");
    };
    const actionable = (g) => g.items.filter(i => ["draft", "blocked", "failed"].includes(i.status)).map(i => i.id);
    const allIds = groups.flatMap(actionable);
    const counts = data?.status_counts || {};

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <OutboundHeader icon={Inbox} title="Outbox"
                    subtitle="Nothing goes to a prospect until it is approved here. Read each email as they will: is every fact true, and would you send it yourself?">
                    {status === "draft" && allIds.length > 0 && (
                        <Btn variant="primary" onClick={() => act("approve", allIds)}><Check className="h-3.5 w-3.5" /> Approve all {allIds.length} shown</Btn>
                    )}
                </OutboundHeader>

                <div className="flex flex-wrap gap-1">
                    {STATUSES.map(([v, label]) => {
                        const n = v === "all" ? null : v.split(",").reduce((a, k) => a + (counts[k] || 0), 0);
                        return (
                            <button key={v} onClick={() => { const p = new URLSearchParams(params); p.set("status", v); setParams(p); }}
                                className={cn("rounded-full px-3 py-1 text-xs border",
                                    status === v ? "border-primary bg-primary/10 text-primary" : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300")}>
                                {label}{n !== null ? ` (${n})` : ""}
                            </button>
                        );
                    })}
                    {sequence && (
                        <button onClick={() => { const p = new URLSearchParams(params); p.delete("sequence"); setParams(p); }}
                            className="rounded-full px-3 py-1 text-xs border border-gray-300 text-gray-600 inline-flex items-center gap-1">
                            one sequence <X className="h-3 w-3" />
                        </button>
                    )}
                </div>

                {!data ? <p className="text-xs text-gray-400">Loading…</p> : groups.length === 0 ? (
                    <p className="text-xs text-gray-400 py-6 text-center">Nothing here.</p>
                ) : groups.map(g => (
                    <div key={g.enrollment_id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3 text-xs">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">{g.prospect_name}</p>
                                <p className="text-gray-500 flex items-center gap-1.5">to {g.to} <Pill tone={VERIFY_TONE[g.to_verification]}>{g.to_verification}</Pill> · {g.sequence_name}</p>
                            </div>
                            {actionable(g).length > 0 && (
                                <div className="flex gap-2">
                                    <Btn variant="primary" onClick={() => act("approve", actionable(g))}><Check className="h-3.5 w-3.5" /> Approve {actionable(g).length > 1 ? "all" : ""}</Btn>
                                    <Btn variant="danger" onClick={() => act("reject", actionable(g))}><X className="h-3.5 w-3.5" /> Reject</Btn>
                                </div>
                            )}
                        </div>
                        <div className="grid gap-3 xl:grid-cols-3">
                            {g.items.map(i => <ItemCard key={i.id} item={i} onSaved={load} />)}
                        </div>
                    </div>
                ))}
            </div>
        </ContainerCard>
    );
}
